import { MAX_ENTITIES, resource, type Plugin, type State, type System } from '@multiplekex/shallot';
import { setTraits, type FieldAccessor } from '@multiplekex/shallot';
import { Compute, ComputePlugin, createEntityIdBuffer } from '@multiplekex/shallot';
import {
  Render,
  RenderPlugin,
  Pass,
  registerDraw,
  type Draw,
  type SharedPassContext,
} from '@multiplekex/shallot';
import { Transform } from '@multiplekex/shallot';

export const GradientLegendData = {
  data: new Float32Array(MAX_ENTITIES * 4),
};

interface GradientLegendProxy extends Array<number>, FieldAccessor {}

function legendProxy(offset: number): GradientLegendProxy {
  const data = GradientLegendData.data;

  function getValue(eid: number): number {
    return data[eid * 4 + offset];
  }

  function setValue(eid: number, value: number): void {
    data[eid * 4 + offset] = value;
  }

  return new Proxy([] as unknown as GradientLegendProxy, {
    get(_, prop) {
      if (prop === 'get') return getValue;
      if (prop === 'set') return setValue;
      const eid = Number(prop);
      if (Number.isNaN(eid)) return undefined;
      return getValue(eid);
    },
    set(_, prop, value) {
      const eid = Number(prop);
      if (Number.isNaN(eid)) return false;
      setValue(eid, value);
      return true;
    },
  });
}

export const GradientLegend: {
  transition: GradientLegendProxy;
  width: GradientLegendProxy;
  height: GradientLegendProxy;
} = {
  transition: legendProxy(0),
  width: legendProxy(1),
  height: legendProxy(2),
};

setTraits(GradientLegend, {
  defaults: () => ({
    transition: 0,
    width: 0.5,
    height: 5,
  }),
});

export interface GradientLegendConfig {
  scene: GPUBuffer;
  legends: GPUBuffer;
  entityIds: GPUBuffer;
  matrices: GPUBuffer;
  getCount: () => number;
}

export const gradientLegendShader = /* wgsl */ `
struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) uv: vec2<f32>,
  @location(1) transition: f32,
}

struct Scene {
  viewProj: mat4x4<f32>,
  cameraWorld: mat4x4<f32>,
  ambientColor: vec4<f32>,
  sunDirection: vec4<f32>,
  sunColor: vec4<f32>,
  cameraMode: f32,
  cameraSize: f32,
  viewport: vec2<f32>,
}

struct LegendData {
  transition: f32,
  width: f32,
  height: f32,
  _pad: f32,
}

@group(0) @binding(0) var<uniform> scene: Scene;
@group(0) @binding(1) var<storage, read> entityIds: array<u32>;
@group(0) @binding(2) var<storage, read> legends: array<LegendData>;
@group(0) @binding(3) var<storage, read> matrices: array<mat4x4<f32>>;

@vertex
fn vs(@builtin(vertex_index) vid: u32, @builtin(instance_index) iid: u32) -> VertexOutput {
  let eid = entityIds[iid];
  let legend = legends[eid];
  let transform = matrices[eid];

  let halfW = legend.width * 0.5 * legend.transition;
  let halfH = legend.height * 0.5 * legend.transition;

  var localPos: vec2<f32>;
  var uv: vec2<f32>;
  switch vid {
    case 0u: { localPos = vec2(-halfW, -halfH); uv = vec2(0.0, 0.0); }
    case 1u: { localPos = vec2(halfW, -halfH); uv = vec2(1.0, 0.0); }
    case 2u: { localPos = vec2(halfW, halfH); uv = vec2(1.0, 1.0); }
    case 3u: { localPos = vec2(-halfW, -halfH); uv = vec2(0.0, 0.0); }
    case 4u: { localPos = vec2(halfW, halfH); uv = vec2(1.0, 1.0); }
    case 5u: { localPos = vec2(-halfW, halfH); uv = vec2(0.0, 1.0); }
    default: { localPos = vec2(0.0, 0.0); uv = vec2(0.0, 0.0); }
  }

  let worldPos = transform[3].xyz + vec3(localPos, 0.0);
  let clipPos = scene.viewProj * vec4(worldPos, 1.0);

  var out: VertexOutput;
  out.position = clipPos;
  out.uv = uv;
  out.transition = legend.transition;
  return out;
}

struct FragmentOutput {
  @location(0) color: vec4<f32>,
  @location(1) mask: f32,
}

@fragment
fn fs(input: VertexOutput) -> FragmentOutput {
  let t = input.uv.y;

  // Nonlinear: Red -> Yellow at 90%, Yellow -> Green in top 10%
  var color: vec3<f32>;
  let red = vec3(0.863, 0.345, 0.345);
  let yellow = vec3(0.898, 0.627, 0.251);
  let green = vec3(0.247, 0.722, 0.478);
  if (t < 0.9) {
    color = mix(red, yellow, t / 0.9);
  } else {
    color = mix(yellow, green, (t - 0.9) / 0.1);
  }

  var out: FragmentOutput;
  out.color = vec4(color, input.transition);
  out.mask = select(0.0, 1.0, input.transition > 0.01);
  return out;
}
`;

export function createGradientLegendPipeline(
  device: GPUDevice,
  format: GPUTextureFormat,
  maskFormat: GPUTextureFormat
): GPURenderPipeline {
  const module = device.createShaderModule({ code: gradientLegendShader });

  return device.createRenderPipeline({
    layout: 'auto',
    vertex: {
      module,
      entryPoint: 'vs',
    },
    fragment: {
      module,
      entryPoint: 'fs',
      targets: [
        {
          format,
          blend: {
            color: {
              srcFactor: 'src-alpha',
              dstFactor: 'one-minus-src-alpha',
              operation: 'add',
            },
            alpha: {
              srcFactor: 'one',
              dstFactor: 'one-minus-src-alpha',
              operation: 'add',
            },
          },
        },
        {
          format: maskFormat,
          writeMask: GPUColorWrite.RED,
        },
      ],
    },
    primitive: {
      topology: 'triangle-list',
    },
    depthStencil: {
      format: 'depth24plus',
      depthCompare: 'less',
      depthWriteEnabled: false,
    },
  });
}

function createGradientLegendDraw(config: GradientLegendConfig): Draw {
  let pipeline: GPURenderPipeline | null = null;
  let bindGroup: GPUBindGroup | null = null;

  return {
    id: 'gradient-legend',
    pass: Pass.Overlay,
    order: 1,

    execute() {},

    draw(pass: GPURenderPassEncoder, ctx: SharedPassContext) {
      const count = config.getCount();
      if (count === 0) return;

      if (!pipeline) {
        pipeline = createGradientLegendPipeline(ctx.device, ctx.format, ctx.maskFormat);
      }

      if (!bindGroup) {
        bindGroup = ctx.device.createBindGroup({
          layout: pipeline.getBindGroupLayout(0),
          entries: [
            { binding: 0, resource: { buffer: config.scene } },
            { binding: 1, resource: { buffer: config.entityIds } },
            { binding: 2, resource: { buffer: config.legends } },
            { binding: 3, resource: { buffer: config.matrices } },
          ],
        });
      }

      pass.setPipeline(pipeline);
      pass.setBindGroup(0, bindGroup);
      pass.draw(6, count);
    },
  };
}

export interface GradientLegendState {
  buffer: GPUBuffer;
  entityIds: GPUBuffer;
  count: number;
}

export const GradientLegends = resource<GradientLegendState>('gradient-legends');

const entityIdArray = new Uint32Array(MAX_ENTITIES);

const GradientLegendSystem: System = {
  group: 'draw',

  update(state: State) {
    const compute = Compute.from(state);
    const legends = GradientLegends.from(state);
    if (!compute || !legends) return;

    const { device } = compute;

    let count = 0;
    for (const eid of state.query([GradientLegend, Transform])) {
      if (GradientLegend.transition[eid] <= 0) continue;
      entityIdArray[count++] = eid;
    }

    device.queue.writeBuffer(legends.buffer, 0, GradientLegendData.data);
    device.queue.writeBuffer(legends.entityIds, 0, entityIdArray, 0, count);
    legends.count = count;
  },
};

export const GradientLegendPlugin: Plugin = {
  systems: [GradientLegendSystem],
  components: { GradientLegend },
  dependencies: [ComputePlugin, RenderPlugin],

  initialize(state: State) {
    const compute = Compute.from(state);
    const render = Render.from(state);
    if (!compute || !render) return;

    const { device } = compute;

    const legendState: GradientLegendState = {
      buffer: device.createBuffer({
        label: 'gradient-legends',
        size: MAX_ENTITIES * 4 * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      }),
      entityIds: createEntityIdBuffer(device, MAX_ENTITIES),
      count: 0,
    };

    state.setResource(GradientLegends, legendState);

    registerDraw(
      state,
      createGradientLegendDraw({
        scene: render.scene,
        legends: legendState.buffer,
        entityIds: legendState.entityIds,
        matrices: render.matrices,
        getCount: () => legendState.count,
      })
    );
  },
};
